import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiDatabase, FiHeart, FiMonitor, FiShield, FiTrendingUp, FiUsers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Navbar';

const Healthcare = () => {
  const solutions = [
    {
      title: "Electronic Health Records (EHR) Integration",
      description: "Comprehensive digital health record systems that improve patient care coordination and clinical decision-making.",
      icon: FiDatabase,
      features: ["Patient Data Management", "Clinical Documentation", "Interoperability", "Analytics Dashboard"]
    },
    {
      title: "Telemedicine & Remote Patient Monitoring Platforms",
      description: "Secure, HIPAA-compliant telehealth solutions enabling remote patient consultations and care delivery.",
      icon: FiMonitor,
      features: ["Video Consultations", "Remote Monitoring", "Prescription Management", "Secure Messaging"]
    },
    {
      title: "Healthcare Analytics",
      description: "Advanced data analytics solutions providing insights for population health, quality improvement, and operational efficiency.",
      icon: FiTrendingUp,
      features: ["Population Health Analytics", "Quality Metrics", "Predictive Analytics", "Cost Analysis"]
    },
    {
      title: "Patient Management Systems",
      description: "Integrated systems for patient registration, scheduling, billing, and care coordination across healthcare facilities.",
      icon: FiUsers,
      features: ["Appointment Scheduling", "Patient Portal", "Billing Integration", "Care Coordination"]
    }
  ];

  const caseStudies = [
    {
      title: "Regional Hospital Network EHR Implementation",
      challenge: "5-hospital network needed unified EHR system for 200+ physicians",
      solution: "Implemented Epic EHR with custom workflows and integration modules",
      results: ["30% reduction in documentation time", "25% improvement in patient satisfaction", "40% faster lab result turnaround"]
    },
    {
      title: "Telehealth Platform for Rural Healthcare",
      challenge: "Rural healthcare provider needed to extend services to remote areas",
      solution: "Built custom telemedicine platform with mobile app and remote monitoring",
      results: ["50+ remote consultations monthly", "60% reduction in travel time for patients", "95% patient satisfaction rate"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-blue-600 to-cyan-600">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/80 to-cyan-600/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/expertise"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Expertise</span>
                  </Link>
                  <span>•</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full font-accent tracking-wide">
                    Primary Expertise
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <FiHeart className="text-white" size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight font-display tracking-tight">
                      Healthcare
                    </h1>
                    <p className="text-xl text-blue-100 font-body font-light tracking-wide">
                      Transforming Patient Care Through Technology
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide">
                Healthcare Innovation
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                Revolutionizing
                <span className="block bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  Patient Care
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 font-body font-light leading-relaxed">
                We specialize in developing cutting-edge healthcare technology solutions that improve patient outcomes, 
                enhance operational efficiency, and ensure regulatory compliance across the healthcare ecosystem.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-blue-100">
                  <div className="text-3xl font-bold text-blue-600 font-display">10+</div>
                  <div className="text-sm text-gray-600 font-body">Healthcare Projects</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-blue-100">
                  <div className="text-3xl font-bold text-cyan-600 font-display">100k+</div>
                  <div className="text-sm text-gray-600 font-body">Patient Records</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-blue-100">
                  <div className="text-3xl font-bold text-blue-600 font-display">25+</div>
                  <div className="text-sm text-gray-600 font-body">Healthcare Providers</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-blue-100">
                  <div className="text-3xl font-bold text-cyan-600 font-display">99.9%</div>
                  <div className="text-sm text-gray-600 font-body">System Uptime</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: FiShield, title: "HIPAA Compliance", desc: "Ensuring patient data security and privacy" },
                { icon: FiUsers, title: "Patient-Centric Design", desc: "Solutions focused on improving patient experience" },
                { icon: FiTrendingUp, title: "Operational Efficiency", desc: "Streamlining healthcare workflows and processes" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg border border-blue-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Our Healthcare
              <span className="block bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive technology solutions designed specifically for healthcare organizations and their unique challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl border border-blue-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <solution.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-gray-700 font-body leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Success
              <span className="block bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Real-world examples of how our healthcare solutions have transformed patient care and operational efficiency.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-3xl shadow-xl border border-blue-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                  {study.title}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Challenge</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Solution</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 font-heading tracking-wide">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full"></div>
                          <span className="text-gray-700 font-body">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Transform Your Healthcare Organization?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our healthcare expertise can improve patient outcomes and operational efficiency for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your Healthcare Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Healthcare; 